import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  useIsFetching,
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef, useState } from "react";

import {
  updateSettings,
  type PlatformSettingsUpdate,
  type ValidatedPlatformSettings,
} from "../api";
import {
  reconcilePlatformSettings,
  retryPlatformSettingsRead,
  SettingsAuthorityError,
  PLATFORM_SETTINGS_MUTATION_KEY,
  PLATFORM_SETTINGS_QUERY_KEY,
} from "../settingsAuthority";
import {
  useSettingsAuthority,
  type SettingsDesiredMutation,
  type SettingsWriteLease,
} from "../settingsAuthorityStore";
import { settingsUpdateErrorMessage } from "../settingsErrors";

function describeUpdateError(error: unknown): string {
  return settingsUpdateErrorMessage(
    error,
    "Failed to update the feature flag.",
  );
}

const RECONCILIATION_ERROR =
  "The feature flag change could not be confirmed. Retry to refresh settings.";

export function FeatureFlagsPanel({
  settings,
}: {
  settings: ValidatedPlatformSettings;
}) {
  const queryClient = useQueryClient();
  const authority = useSettingsAuthority(queryClient);
  useIsFetching({ exact: true, queryKey: PLATFORM_SETTINGS_QUERY_KEY });
  const settingsWriteCount = useIsMutating({
    exact: true,
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
  });
  const initialResolutionSequence = useRef(authority.resolution?.sequence ?? 0);
  const [pendingFlag, setPendingFlag] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const authorityRecovery =
    authority.recovery?.kind === "feature-flag" ? authority.recovery : null;
  const authorityResolution =
    authority.resolution &&
    authority.resolution.sequence > initialResolutionSequence.current &&
    authority.resolution.desired.kind === "feature-flag"
      ? authority.resolution
      : null;
  const authorityResolutionDesired =
    authorityResolution?.desired.kind === "feature-flag"
      ? authorityResolution.desired
      : null;
  const settingsReadUncertain =
    queryClient.getQueryState(PLATFORM_SETTINGS_QUERY_KEY)?.status === "error";
  const writesBlocked =
    authority.recovery !== null ||
    authority.refreshing ||
    authority.writing ||
    settingsWriteCount > 0 ||
    settingsReadUncertain;

  const mutation = useMutation({
    mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
    mutationFn: async ({
      lease,
      payload,
    }: {
      lease: SettingsWriteLease;
      payload: PlatformSettingsUpdate;
    }) => {
      await updateSettings(payload);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return reconcilePlatformSettings(queryClient, () =>
        authority.isWriteOperationCurrent(lease),
      );
    },
  });

  const flags = Object.entries(settings.feature_flags);
  const activeFlag =
    pendingFlag ??
    authorityRecovery?.key ??
    (authorityResolution?.outcome === "diverged"
      ? authorityResolutionDesired?.key
      : null);
  const pendingValue = activeFlag
    ? settings.feature_flags[activeFlag]
    : undefined;
  const desiredValue = authorityRecovery
    ? authorityRecovery.desiredValue
    : authorityResolution?.outcome === "diverged"
      ? authorityResolutionDesired?.desiredValue
      : pendingValue === undefined
        ? undefined
        : !pendingValue;
  const displayedUpdateError = authorityRecovery
    ? RECONCILIATION_ERROR
    : authorityResolution?.outcome === "diverged"
      ? "The saved feature flag state differs from this request. Review it before trying again."
      : updateError;

  function openConfirmation(key: string) {
    if (mutation.isPending || writesBlocked) return;
    setPendingFlag(key);
    setUpdateError(null);
  }

  function closeConfirmation() {
    if (mutation.isPending || writesBlocked) {
      return;
    }
    if (authorityResolution?.outcome === "diverged") {
      authority.clearResolution(authorityResolution);
    }
    setPendingFlag(null);
    setUpdateError(null);
  }

  async function confirmUpdate() {
    if (
      !activeFlag ||
      pendingValue === undefined ||
      mutation.isPending ||
      queryClient.isMutating({
        exact: true,
        mutationKey: PLATFORM_SETTINGS_MUTATION_KEY,
      }) > 0 ||
      !authority.tryStartWrite()
    ) {
      return;
    }
    const lease = authority.beginWriteOperation();
    if (!lease) {
      authority.finishWrite();
      return;
    }
    setUpdateError(null);
    const featureFlags = Object.freeze({
      ...settings.feature_flags,
      [activeFlag]: !pendingValue,
    });
    const requestedFlag = activeFlag;
    const desired = Object.freeze({
      desiredValue: !pendingValue,
      key: requestedFlag,
      kind: "feature-flag",
    }) satisfies SettingsDesiredMutation;
    const payload = Object.freeze({
      feature_flags: featureFlags,
    }) satisfies PlatformSettingsUpdate;
    try {
      const authoritative = await mutation.mutateAsync({ lease, payload });
      if (!authoritative || !authority.isWriteOperationCurrent(lease)) {
        return;
      }
      if (authoritative.feature_flags[requestedFlag] === desiredValue) {
        authority.recordResolution(desired, "confirmed");
        setPendingFlag(null);
      } else {
        authority.recordResolution(desired, "diverged");
      }
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      if (error instanceof SettingsAuthorityError) {
        authority.recordRecovery(desired);
      } else {
        setUpdateError(describeUpdateError(error));
      }
    } finally {
      authority.finishWrite(lease);
    }
  }

  async function retryAuthorityRead() {
    if (!authorityRecovery || mutation.isPending) return;
    const recovery = authorityRecovery;
    if (!authority.tryStartRefresh(recovery)) return;
    try {
      const authoritative = await retryPlatformSettingsRead(queryClient);
      const confirmed =
        authoritative.feature_flags[recovery.key] === recovery.desiredValue;
      authority.resolveRecovery(recovery, confirmed ? "confirmed" : "diverged");
      if (confirmed) {
        setPendingFlag(null);
        setUpdateError(null);
      } else {
        setPendingFlag(recovery.key);
      }
    } catch {
      authority.finishRefresh(recovery);
    }
  }

  const action = desiredValue ? "Enable" : "Disable";

  return (
    <>
      <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            Feature flags
          </Typography>

          {flags.length === 0 ? (
            <Typography color="text.secondary" variant="body2">
              No feature flags configured.
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />}>
              {flags.map(([key, value]) => (
                <Stack
                  key={key}
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.25,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                      {key}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color={value ? "success" : "inherit"}
                    disabled={mutation.isPending || writesBlocked}
                    aria-label={`${value ? "Disable" : "Enable"} ${key} feature flag`}
                    onClick={() => openConfirmation(key)}
                  >
                    {value ? "Enabled" : "Disabled"}
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={activeFlag !== null}
        onClose={closeConfirmation}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {activeFlag ? `${action} feature flag “${activeFlag}”?` : ""}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This changes the saved platform setting for all users.
          </DialogContentText>
          {displayedUpdateError ? (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                authorityRecovery ? (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={authority.refreshing}
                    onClick={() => void retryAuthorityRead()}
                  >
                    {authority.refreshing ? "Retrying…" : "Retry"}
                  </Button>
                ) : undefined
              }
            >
              {displayedUpdateError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            disabled={mutation.isPending || writesBlocked}
            onClick={closeConfirmation}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={mutation.isPending || writesBlocked}
            onClick={() => void confirmUpdate()}
          >
            {mutation.isPending ? "Saving…" : action}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
